<script lang="ts">
  import { ChartComponent as Chart, type TraceList, type DisplayUnitPreference } from 'libchartium';
  import { getContext } from '#lib/utils/svelte-context.js';
  import { CommonRulerContextKey } from './contexts/CommonRulerContext.svelte';
  import { YAxisAlignmentContextKey } from './contexts/YAxisAlignmentContext.svelte';
  import { isElement } from '@typek/typek';
  import { CommonXRangeContextKey } from './contexts/CommonXRangeContext.svelte';
  import { GraphsMountedContextKey } from '../../routes/(admin)/+layout.svelte';
  import { onMount } from 'svelte';
  import { syncTimeRange$ } from '#lib/timeRange.js';
  import { getTraceColor } from '#lib/overview/colorPalette.js';

  const commonXRuler$ = getContext(CommonRulerContextKey);
  const commonYAxisWidth$ = getContext(YAxisAlignmentContextKey);
  const graphsMounted$ = getContext(GraphsMountedContextKey);
  const commonXRange$ = getContext(CommonXRangeContextKey);
  const graphIdentifier = Symbol('graphIdentifier');

  interface Props {
    traces: TraceList;
    title?: string;
    displayUnit?: DisplayUnitPreference;
    kind?: 'xy'; // | 'bar' | 'pie';
    yToZero?: boolean;
    shortenToolbar?: boolean;
    class?: string;
  }

  let {
    traces,
    title = '',
    displayUnit = 'data',
    yToZero = false,
    shortenToolbar = false,
    class: klass = '',
  }: Props = $props();

  onMount(() => {
    graphsMounted$.update((set) => {
      set.add(graphIdentifier);
      return set;
    });
    return () => {
      graphsMounted$.update((set) => {
        set.delete(graphIdentifier);
        return set;
      });
    };
  });

  let coloredTraces = $derived(
    traces.withStyle(
      Object.fromEntries(
        [...traces.traces()]
          .map((id) => ({ id, label: traces.getLabel(id) ?? id }))
          .filter(({ label }) => getTraceColor(label) !== undefined)
          .map(({ id, label }) => [id, { color: getTraceColor(label) }]),
      ),
    ),
  );
</script>

<div class="plot">
  <div class="plot-wrapper-outer">
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="plot-wrapper-inner"
      onpointerdown={(e) => {
        if (isElement(e.target) && e.target.matches('.plot .title')) {
          // Grid item drag initiated – don't block
        } else {
          // Don't drag this item
          e.stopPropagation();
        }
      }}
    >
      <Chart
        traces={coloredTraces}
        {title}
        class={klass + ' bg-base-100 z-30'}
        defaultYUnit={displayUnit}
        {commonXRuler$}
        doUseCommonXRange$={syncTimeRange$}
        legendPosition="bottom"
        legendPreviewStyle="simplified"
        hoverPointsInterpolation="nearest"
        showYAxisZero={yToZero}
        hideToolbarUntilHover={shortenToolbar}
        {commonXRange$}
        commonYAxisWidth$={commonYAxisWidth$ as any}
        chartStylesheet={{
          'bubbles.y': {
            scaleToFitAxis: true,
          },
        }}
      />
    </div>
  </div>
</div>

<style>
  .plot {
    display: flex;
    flex-direction: column;
    height: 100%;
    /* padding-right: 2em; */
    overflow: hidden;
  }

  .plot-wrapper-outer {
    flex-grow: 1;
    position: relative;
  }

  .plot-wrapper-inner {
    height: 100%;
  }

  .plot :global(.title) {
    cursor: move;
  }
</style>
